import { env } from 'configs/EnvironmentConfig'
import fetch from 'auth/FetchInterceptor'
import { z } from 'zod'
import { Validate } from './SchemaValidation'

const AutomationService = {}

const automationSchema = z.object({
  name: z.string(),
  description: z.string(),
  template: z.string(),
})

const updateAutomationSchema = z.object({
  name: z.string(),
  description: z.string(),
})

const numberId = z.number()
const stringId = z.string()

const searchQuerySchema = z.object({
  q: z.string().optional(),
  categories: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  page: z.number().optional(),
  page_size: z.number().optional(),
})

// Credential schema
const baseCredentialSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
})

const passwordCredentialSchema = baseCredentialSchema.extend({
  credential_type_id: z.literal(1),
  name: z.string().trim(),
  user_name: z.string().trim().min(1, 'Username is required'),
  password: z.string().trim().min(1, 'Password is required'),
  description: z.string().trim(),
})
const sshCredentialSchema = baseCredentialSchema.extend({
  credential_type_id: z.literal(2),
  name: z.string().trim(),
  user_name: z.string().trim().min(1, 'Username is required'),
  ssh_private_key: z.string().trim().min(1, 'SSH private key is required'),
  ssh_key_passphrase: z.string().optional(),
  description: z.string().trim(),
})

const credentialSchema = z.discriminatedUnion('credential_type_id', [
  passwordCredentialSchema,
  sshCredentialSchema,
])

// List automations
AutomationService.getAutomationList = (page = 1, limit = 10) => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation`,
    method: 'GET',
    params: { page, limit },
  })
}

// Get automation details
AutomationService.getAutomation = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}`,
    method: 'GET',
  })
}

// Create automation from template
AutomationService.createAutomation = data => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation`,
    method: 'POST',
    data: Validate(automationSchema, data),
  })
}

// Update automation
AutomationService.updateAutomation = (id, data) => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}`,
    method: 'PUT',
    data: Validate(updateAutomationSchema, data),
  })
}

// Delete automation
AutomationService.deleteAutomation = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}`,
    method: 'DELETE',
  })
}

// Execute automation
AutomationService.executeAutomation = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}/execute`,
    method: 'POST',
  })
}

// Get execution status
AutomationService.getExecutionStatus = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}/status`,
    method: 'GET',
  })
}

// Get execution logs
AutomationService.getExecutionLogs = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/automation/${Validate(
      numberId,
      id
    )}/logs`,
    method: 'GET',
  })
}

// List catalog items
AutomationService.getCatalogList = (page = 1, limit = 10) => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/catalog`,
    method: 'GET',
    params: { page, limit },
  })
}

// Get catalog item details
AutomationService.getCatalogItem = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/catalog/${Validate(numberId, id)}`,
    method: 'GET',
  })
}

// Get categories
AutomationService.getCategoryList = () => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/category`,
    method: 'GET',
  })
}

// Search catalog
AutomationService.searchCatalog = query => {
  const validatedQuery = Validate(searchQuerySchema, query)
  const params = new URLSearchParams()

  if (validatedQuery.q) params.append('q', validatedQuery.q)
  if (validatedQuery.page) params.append('page', validatedQuery.page)
  if (validatedQuery.page_size)
    params.append('page_size', validatedQuery.page_size)
  if (validatedQuery.categories) {
    validatedQuery.categories.forEach(category =>
      params.append('categories', category)
    )
  }
  if (validatedQuery.tags) {
    validatedQuery.tags.forEach(tag => params.append('tags', tag))
  }

  return fetch({
    url: `${
      env.AUTOMATION_ENDPOINT_URL
    }/api/catalog/search?${params.toString()}`,
    method: 'GET',
  })
}

// Get credential list
AutomationService.getCredentialList = () => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/credential`,
    method: 'GET',
  })
}

AutomationService.createCredential = data => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/credential`,
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    data: Validate(credentialSchema, data),
  })
}

// Delete credential
AutomationService.deleteCredential = id => {
  return fetch({
    url: `${env.AUTOMATION_ENDPOINT_URL}/api/credential/${Validate(
      stringId,
      id
    )}`,
    method: 'DELETE',
  })
}

export default AutomationService
